import type { PermissionKey } from '@warehouse/shared';

import { areaModules } from './areas';
import type { AreaNavItem, NavSectionId, PortalNavItem } from './area';

/**
 * Sidebar composition. The SHELL owns the section list, their order and their
 * headings; each area only declares which section its items belong to
 * (`src/app/area.ts`). Adding a page therefore never touches this file.
 *
 * `overview` and `system` have no visible heading — a one-item group with a
 * label reads as clutter in a sidebar.
 */

export interface NavSection {
  id: NavSectionId;
  /** Namespaced i18n key, or `null` for an unlabelled group. */
  labelKey: string | null;
  items: AreaNavItem[];
}

const SECTION_ORDER: { id: NavSectionId; labelKey: string | null }[] = [
  { id: 'overview', labelKey: null },
  { id: 'orders', labelKey: 'common:nav.orders.section' },
  { id: 'warehouse', labelKey: 'common:nav.warehouse.section' },
  { id: 'production', labelKey: 'common:nav.production.section' },
  { id: 'registers', labelKey: 'common:nav.masterData.section' },
  { id: 'devices', labelKey: 'common:nav.devices.section' },
  { id: 'integration', labelKey: 'common:nav.integration.section' },
  { id: 'system', labelKey: null },
];

const DEFAULT_ORDER = 100;

/** Every declared nav item, flattened, in section then `order` sequence. */
export function allNavItems(): AreaNavItem[] {
  return areaModules.flatMap((area) => area.nav ?? []);
}

export function allPortalNavItems(): PortalNavItem[] {
  return areaModules
    .flatMap((area) => area.portalNav ?? [])
    .sort((a, b) => (a.order ?? DEFAULT_ORDER) - (b.order ?? DEFAULT_ORDER));
}

/**
 * Build the sidebar for one user. `can` is the permission predicate from the
 * auth store (super_admin already short-circuits inside it). Sections that end
 * up empty are dropped, so a `bestallare` who somehow lands on the admin app
 * sees no orphan headings.
 */
export function buildNavSections(can: (permissions?: readonly PermissionKey[]) => boolean): NavSection[] {
  const items = allNavItems().filter((item) => can(item.permissions));

  return SECTION_ORDER.map(({ id, labelKey }) => ({
    id,
    labelKey,
    items: items
      .filter((item) => item.section === id)
      .sort((a, b) => (a.order ?? DEFAULT_ORDER) - (b.order ?? DEFAULT_ORDER)),
  })).filter((section) => section.items.length > 0);
}

export function buildPortalNav(can: (permissions?: readonly PermissionKey[]) => boolean): PortalNavItem[] {
  return allPortalNavItems().filter((item) => can(item.permissions));
}
